import React, { useEffect, useRef, useState } from 'react';
import Yup from '~yup';

import { useFormik } from 'formik';

import Text from '~components/atoms/Text';
import ActionButton from '~components/atoms/ActionButton';
import LabelledInput from '~components/atoms/LabelledInput';
import LabelledDropdown from '~components/atoms/LabelledDropdown';

import combinedActions from '~redux/combinedActions';
import { useAppDispatch, useAppSelector } from '~redux/hooks';

import {
    BranchStatusEnum,
    CustomerAccFundTypeEnum,
    CustomerAccTypeEnum,
    GoldTypeEnum,
    MetalTypeEnum,
    SilverTypeEnum,
} from '~utils/enums';

import { TDropdownOption } from '~types/components/labelledDropdown';

import UtilFunctions from '~utils/functions';

import * as FormStyles from './_createGoldConversionForm.module.scss';

import { TInventoryFilter } from '~types/feat/gold';

const CreateInventoryInitialValues = {
    customerId: 0,
    name: '',
    idNo: '',
    CIFNo: '',
    branchName: '',
    branchCode: '',
    region: '',
    originProductId: '',
    availablePaperGold: '',
    availablePaperSilver: '',
    targetProductId: '',
    quantity: '',
    serialNo: '',
    salesPersonnelName: '',
    referralName: '',
    metalType: '',
    conversionFee: '',
};

const CreateGoldConversionForm = () => {
    const dispatch = useAppDispatch();

    const {
        Misc: { fixedDecimal },
    } = UtilFunctions;

    const searchCustomerIsLoading = useAppSelector(state => state.customer.loadings.getAllCustomers);

    const createConversionFinalDraftsIsLoading = useAppSelector(state => state.gold.loadings.createConversionFinalDrafts);

    const branchesList = useAppSelector(state => state.branch.allBranches);
    const regionList = useAppSelector(state => state.branch.allRegions);

    const inventoryList = useAppSelector(state => state.gold.allInventories);

    const serialNumberList = useAppSelector(state => state.gold.allSerialNumbers);

    const customerList = useAppSelector(state => state.customer.allCustomers);
    const customerDetails = useAppSelector(state => state.customer.customerDetails);

    const goldConversionDraftId = useAppSelector(state => state.gold.goldConversionDraftId);
    const goldConversionDraftsFee = useAppSelector(state => state.gold.goldConversionDraftsFee);
    const goldConversionFinalDraftsParams = useAppSelector(state => state.gold.params.goldConversionFinalDraftsParams);

    const inventoryFilterParams = useAppSelector(state => state.gold.params.inventoryFilterParams);

    const customerInventory = useAppSelector(state => state.customer.customerInventory);

    const { allBranchAccess, branchCode } = useAppSelector(state => state.auth.userDetails);

    const [disableCustomerSearch, setDisableCustomerSearch] = useState(false);
    const [gettingCustomers, setGettingCustomers] = useState(false);
    const [name, setName] = useState('');
    const [selectedBranchCode, setSelectedBranchCode] =useState('');
    const [targetProductName, setTargetProductName] =useState('');
    const isChangingMetalType = useRef(false);
    const isChangingCustomer = useRef(false);

    const validationSchema = Yup.object().shape({
        name: Yup.string()
            .matchNoLeadingAndTrailingWhiteSpaces()
            .matchMaxOneWhiteSpace()
            .required('Customer name is required'),
        idNo: Yup.string()
            .matchNoLeadingAndTrailingWhiteSpaces()
            .required('Customer ID is required'),
        CIFNo: Yup.string()
            .matchNoLeadingAndTrailingWhiteSpaces()
            .required('Customer CIF is required'),
        branchName: Yup.string()
            .matchNoLeadingAndTrailingWhiteSpaces()
            .matchMaxOneWhiteSpace()
            .test(
                'check-branch-name',
                'Selected branch does not exist',
                function (value) {
                    if (!value) return false;

                    return branchOptions.some(branch => branch.label === value);
                }
            )
            .required('Branch name is required'),
        branchCode: Yup.string().required('Branch code is required'),
        region: Yup.string()
            .matchNoLeadingAndTrailingWhiteSpaces()
            .matchMaxOneWhiteSpace()
            .required('Region is required'),
        originProductId: Yup.string().required('Select origin product'),
        availablePaperGold: Yup.string(),
        availablePaperSilver: Yup.string(),
        targetProductId: Yup.string().required('Select a product'),
        quantity: Yup.number()
            .typeError('Quantity must be a number')
            .test(
                'is-valid-quantity',
                'Quantity must be at least 1',
                function (value) {
                    if (value === undefined || value === null) {
                        return false;
                    }

                    return !isNaN(Number(value));
                }
            )
            .test(
                'no-exponential-notation',
                'Quantity must be a whole number (exponential notation is not allowed)',
                function () {
                    const original = this.originalValue;

                    if (original === undefined || original === null || original === '') {
                        return true;
                    }

                    return !/e/i.test(String(original));
                }
            )
            .test(
                'is-within-balance',
                function (value) {
                    const {
                        metalType,
                        targetProductId,
                        availablePaperGold,
                        availablePaperSilver,
                    } = this.parent;

                    const isSilver = Number(metalType) === Number(MetalTypeEnum.Silver);
                    const availableBalance = Number(isSilver ? availablePaperSilver : availablePaperGold);
                    const quantity = Number(value);

                    if (value === undefined || value === null) {
                        return false;
                    }

                    if (isNaN(quantity)) {
                        return false;
                    }

                    if (isNaN(availableBalance)) {
                        return this.createError({ message: isSilver ? 'Invalid paper silver balance' : 'Invalid paper gold balance'});
                    }

                    const selectedProduct = inventoryList.find(item => Number(item.productId) === Number(targetProductId));

                    if (!selectedProduct) {
                        return this.createError({
                            message: 'Invalid target product',
                        });
                    }

                    const unitWeight = Number(isSilver ? selectedProduct.physicalSilverWeight : selectedProduct.physicalGoldWeight);

                    if (isNaN(unitWeight)) {
                        return this.createError({
                            message: 'Invalid target product weight',
                        });
                    }

                    if (quantity * unitWeight > availableBalance) {
                        return this.createError({
                            message: isSilver
                                ? 'Quantity must not exceed paper silver balance'
                                : 'Quantity must not exceed paper gold balance',
                        });
                    }

                    return true;
                },
            )
            .positive('Quantity must be positive')
            .max(1000000, 'Amount must not exceed 1000000 grams')
            .required('Quantity is required'),
        serialNo: Yup.string()
            .matchNoLeadingAndTrailingWhiteSpaces()
            .matchNoLeadingAndTrailingCommas()
            .matchZeroWhiteSpace()
            .when('targetProductId', {
                is: (val: string) => 
                {
                    const product = inventoryList.find(p => p.productId === Number(val));

                    return (
                        product?.goldType === GoldTypeEnum.PhysicalGold || product?.silverType === SilverTypeEnum.PhysicalSilver
                    );
                },

                then: schema =>
                    schema.test(
                        'serial-numbers-count',
                        function (value) {
                            const { quantity } = this.parent;

                            if (!value) return false;
                            if (quantity === 0) return true;

                            const serialNumbers = value.split(',').map(s => s.trim()).filter(s => s.length > 0);

                            if (serialNumbers.length !== quantity) {
                                return this.createError({
                                    message: `Must provide exactly ${quantity} serial number${quantity !== 1 ? 's' : ''}`,
                                });
                            }

                            return (serialNumbers.length ===quantity);
                        }
                    ).test(
                        'duplicate-serial-numbers',
                        'The provided serial numbers are not unique',
                        function (value) {
                            if (!value) return false;

                            const valuesArray = value.split(',').map(s => s.trim()).filter(s => s.length > 0);

                            return (valuesArray.length === new Set(valuesArray).size);
                        }
                    ).test(
                        'available-serial-numbers',
                        'The provided serial numbers are not available',
                        function (value) {
                            if (!value) return false;
                            if (!serialNumberList) return false;

                            const valuesArray = value.split(',').map(s => s.trim()).filter(s => s.length > 0);

                            return valuesArray.every(serialNo => serialNumberList.some(item => item.serialNo === serialNo));
                        }
                    ),

                otherwise: schema =>
                    schema.test(
                        'serial-numbers-count',
                        'Must provide exactly 1 serial number',
                        function (value) {
                            if (!value) return false;

                            const serialNumbers = value.split(',').map(s => s.trim()).filter(s => s.length > 0);

                            return serialNumbers.length === 1;
                        }
                    ),
            }),
        salesPersonnelName: Yup.string()
            .matchNoLeadingAndTrailingWhiteSpaces()
            .matchMaxOneWhiteSpace(),
        referralName: Yup.string()
            .matchNoLeadingAndTrailingWhiteSpaces()
            .matchMaxOneWhiteSpace(),
        metalType: Yup.number()
            .required('Metal Type is required')
            .min(1, 'Metal Type is required'),
    });

    const branchOptions = branchesList
        .filter(branch =>branch.status === BranchStatusEnum.Active)
        .map(branch => ({
            label: branch.branchName,
            value: branch.branchCode,
        }));

    const customerOptions = customerList.map(customer => ({
        label: `${customer.name} - ${customer.idNo} (${CustomerAccFundTypeEnum[customer.fundType]}) (${CustomerAccTypeEnum[customer.accountType]})${customer.accountType === CustomerAccTypeEnum.Joint ? ' - ' + customer.childName : ''}`,
        value: customer.customerId,
    }));

    const regionOptions = regionList.map(region => ({
        label: region.regionName,
        value: region.regionName,
    }));

    const metalTypeOptions = [
        {
            label: 'Gold',
            value: String(MetalTypeEnum.Gold),
        },
        {
            label: 'Silver',
            value: String(MetalTypeEnum.Silver),
        },
    ];

    const formik = useFormik({
        initialValues: CreateInventoryInitialValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit: values => {
            const branch = branchesList.find(item => item.branchCode === values.branchCode);

            const serialNoIds = values.serialNo
                .replace(/\s+/g, '')
                .split(',')
                .map(serialNo => serialNumberList.find(item => item.serialNo === serialNo)?.id)
                .filter((id): id is number =>id !== undefined);

            dispatch(combinedActions.goldCreateGoldConversionFinalDraftsAttempt(
                {
                    id: goldConversionDraftId.draftId,
                    customerId: Number(values.customerId),
                    branchId: Number(branch?.branchId),
                    originProductId: Number(values.originProductId),
                    targetProductId: Number(values.targetProductId),
                    quantity: Number(values.quantity),
                    serialNoIds,
                    ...(values.salesPersonnelName && {
                        salesPersonnelName:
                            values.salesPersonnelName,
                    }),
                    ...(values.referralName && {
                        referralName:
                            values.referralName,
                    }),
                    metalType: Number(values.metalType) as MetalTypeEnum,
                })
            );
        },
    });

    useEffect(() => {
        let timeout: NodeJS.Timeout;

        if (name && !disableCustomerSearch) {
            setGettingCustomers(true);

            timeout = setTimeout(() => {
                dispatch(combinedActions.customerGetAllCustomersAttempt({searchQuery: name}));
            }, 1000);
        } else {
            setGettingCustomers(false);
        }

        setDisableCustomerSearch(false);

        return () => {
            setGettingCustomers(false);
            clearTimeout(timeout);
        };
    }, [name]);

    useEffect(() => {
        if (!searchCustomerIsLoading) {
            setGettingCustomers(false);
        }
    }, [searchCustomerIsLoading]);

    useEffect(() => {
        const customerId = goldConversionFinalDraftsParams?.customerId;

        if (!customerId) return;
        if (!customerList.length) return;

        const customer = customerList.find(item => item.customerId === customerId);

        if (!customer) return;

        const customerLabel = `${customer.name} - ${customer.idNo} (${CustomerAccFundTypeEnum[customer.fundType]}) (${CustomerAccTypeEnum[customer.accountType]})${
            customer.accountType === CustomerAccTypeEnum.Joint
                ? ' - ' + customer.childName
                : ''
        }`;

        setName(customerLabel);
        setDisableCustomerSearch(true);

        formik.setValues(prev => ({
            ...prev,
            customerId: customer.customerId,
            name: customer.name,
            idNo: customer.idNo,
            CIFNo: customer.CIFNo,
        }));

        dispatch(combinedActions.customerGetCustomerDetailsAttempt({customerId: customer.customerId}));
        dispatch(combinedActions.customerGetCustomerInventoryAttempt({customerId: customer.customerId}));
    }, [goldConversionFinalDraftsParams?.customerId, customerList]);

    useEffect(() => {
        if (!customerDetails) return;

        formik.setFieldValue('availablePaperGold', customerDetails.paperGoldBalance);
        formik.setFieldValue('availablePaperSilver', customerDetails.paperSilverBalance);
    }, [customerDetails]);

    useEffect(() => {
        if (!goldConversionFinalDraftsParams?.branchId) {
            return;
        }

        if (!branchesList.length) {
            return;
        }

        const branch = branchesList.find(item => item.branchId === goldConversionFinalDraftsParams.branchId);

        if (!branch) {
            return;
        }

        const branchRegion = regionList.find(item => item.regionId === branch.regionId);

        setSelectedBranchCode(branch.branchCode);

        formik.setFieldValue('branchName', branch.branchName);
        formik.setFieldValue('branchCode', branch.branchCode);
        formik.setFieldValue('region', branchRegion?.regionName || '');
    }, [goldConversionFinalDraftsParams?.branchId, branchesList, regionList]);

    useEffect(() => {
        if (branchesList.length > 0 && !allBranchAccess && !goldConversionFinalDraftsParams?.branchId) {
            const currentBranch = branchesList.find(branch => branch.branchCode === branchCode);

            if (!currentBranch) return;

            const branchRegion = regionList.find(region => region.regionId === currentBranch.regionId);

            formik.setFieldValue('branchName', currentBranch.branchName);
            formik.setFieldValue('branchCode', currentBranch.branchCode);
            formik.setFieldValue('region', branchRegion?.regionName || '');

            setSelectedBranchCode(currentBranch.branchCode);
        }
    }, [branchesList, regionList, allBranchAccess, branchCode, goldConversionFinalDraftsParams?.branchId]);

    useEffect(() => {
        if (!selectedBranchCode) return;

        const currentMetalType = formik.values.metalType ? (Number(formik.values.metalType) as MetalTypeEnum) : MetalTypeEnum.Gold;

        dispatch(
            combinedActions.goldSetInventoryFilterParams({
                ...inventoryFilterParams,
                branchCode: selectedBranchCode,
                metalType: currentMetalType,
                goldType:
                    currentMetalType ===
                    MetalTypeEnum.Gold
                        ? GoldTypeEnum.PhysicalGold
                        : undefined,
                silverType:
                    currentMetalType ===
                    MetalTypeEnum.Silver
                        ? SilverTypeEnum.PhysicalSilver
                        : undefined,
            })
        );

        dispatch(combinedActions.goldGetAllInventoriesAttempt());

        if (!goldConversionFinalDraftsParams?.targetProductId) {
            formik.setFieldValue('targetProductId', '');
            setTargetProductName('');
        }
    }, [selectedBranchCode, goldConversionFinalDraftsParams?.targetProductId]);

    useEffect(() => {
        if (!customerInventory?.length) return;

        if (goldConversionFinalDraftsParams?.originProductId) {
            const originProduct = customerInventory.find(item => item.productId === goldConversionFinalDraftsParams.originProductId);

            if (!originProduct) return;

            const isSilver = originProduct.silverType === SilverTypeEnum.PaperSilver;
            const metalType = isSilver ? MetalTypeEnum.Silver : MetalTypeEnum.Gold;

            formik.setFieldValue('metalType', String(metalType));
            formik.setFieldValue('originProductId', String(originProduct.productId));

            return;
        }

        const currentMetalType = formik.values.metalType ? (Number(formik.values.metalType) as MetalTypeEnum) : MetalTypeEnum.Gold;

        const paperProduct =
            customerInventory.find(item =>
                currentMetalType ===
                MetalTypeEnum.Silver
                ? item.silverType ===
                    SilverTypeEnum.PaperSilver
                : item.goldType ===
                    GoldTypeEnum.PaperGold
            );

        formik.setFieldValue('originProductId', paperProduct?.productId?.toString() || '');
    }, [customerInventory, goldConversionFinalDraftsParams?.originProductId]);

    useEffect(() => {
        if (!goldConversionFinalDraftsParams) return;

        formik.setFieldValue('quantity', String(goldConversionFinalDraftsParams.quantity));
        formik.setFieldValue('salesPersonnelName', goldConversionFinalDraftsParams.salesPersonnelName || '');
        formik.setFieldValue('referralName', goldConversionFinalDraftsParams.referralName || '');
    }, [goldConversionFinalDraftsParams]);

    useEffect(() => {
        if (isChangingCustomer.current || isChangingMetalType.current) {
            return;
        }
        if (!goldConversionFinalDraftsParams?.targetProductId) {
            return;
        }
        if (!inventoryList.length) {
            return;
        }
        if (!formik.values.branchCode) {
            return;
        }

        const product = inventoryList.find(item =>item.productId === goldConversionFinalDraftsParams.targetProductId);

        if (!product) { return;}

        formik.setFieldValue('targetProductId', String(product.productId));

        setTargetProductName(product.productName);

        dispatch(
            combinedActions.goldSetSerialNumberFilterParams({
                branchCode: formik.values.branchCode,
                productId: product.productId,
            })
        );

        dispatch(combinedActions.goldGetAllSerialNumbersAttempt());
    }, [goldConversionFinalDraftsParams?.targetProductId, inventoryList, formik.values.branchCode]);

    useEffect(() => {
        if (!goldConversionFinalDraftsParams?.serialNoIds?.length) {
            return;
        }

        if (!serialNumberList.length) {
            return;
        }

        const serialNo = goldConversionFinalDraftsParams.serialNoIds
            .map(id => { const serialNumber = serialNumberList.find(item => item.id === id);
                return serialNumber?.serialNo;
            })
            .filter((serial): serial is string => !!serial)
            .join(',');

        if (!serialNo) return;

        formik.setFieldValue('serialNo',serialNo);
    }, [goldConversionFinalDraftsParams?.serialNoIds, serialNumberList]);

    useEffect(() => {
        const draftId = goldConversionDraftId?.draftId;
        const originProductId = Number(formik.values.originProductId);
        const targetProductId = Number(formik.values.targetProductId);
        const quantity = Number(formik.values.quantity);

        if (!draftId || !originProductId || !targetProductId || !quantity) {
            return;
        }

        dispatch(combinedActions.goldGetGoldConversionDraftsFeeAttempt({draftId, originProductId, targetProductId, quantity}));
    }, [goldConversionDraftId?.draftId, formik.values.originProductId, formik.values.targetProductId, formik.values.quantity]);

    const handleCustomerSelected = (customerId: number) => {
        dispatch(combinedActions.customerGetCustomerDetailsAttempt({ customerId }));
        dispatch(combinedActions.customerGetCustomerInventoryAttempt({ customerId }));
        dispatch(combinedActions.goldResetGoldConversionAmount());
        dispatch(combinedActions.goldResetGoldConversionDraftsFee());
    };

    const handleOnSearchCustomer = (event: React.ChangeEvent<HTMLInputElement>) => {
        setName(event.target.value);

        const customer = customerList.find(customerOption => customerOption.idNo === event.target.value);

        if (!customer) return;

        formik.setValues({
            ...CreateInventoryInitialValues,
            branchName: formik.values.branchName,
            branchCode: formik.values.branchCode,
            region: formik.values.region,
            customerId: Number(customer.customerId),
            name: customer.name,
            idNo: customer.idNo,
            CIFNo: customer.CIFNo,
        });
    };

    const handleOnSelectCustomer = (option: TDropdownOption) => {
        const customer = customerList.find(customerOption => customerOption.customerId === option.value);

        if (!customer) return;

        isChangingCustomer.current = true;

        setName(option.label);
        setDisableCustomerSearch(true);
        setTargetProductName('');

        handleCustomerSelected(customer.customerId);

        formik.setValues({
            ...CreateInventoryInitialValues,
            branchName: formik.values.branchName,
            branchCode: formik.values.branchCode,
            region: formik.values.region,
            customerId: Number(customer.customerId),
            name: customer.name,
            idNo: customer.idNo,
            CIFNo: customer.CIFNo,
            originProductId: '',
        });
    };

    const handleOnSearchBranch = (event: React.ChangeEvent<HTMLInputElement>) => {
        formik.handleChange({
            target: {
                name: 'branchName',
                value: event.target.value,
            },
        });

        const branch = branchesList.find(branchOption => branchOption.branchName === event.target.value);
        const branchRegion = regionList.find(regionOption => regionOption.regionId === branch?.regionId);

        if (!branch) {
            dispatch(combinedActions.goldClearInventoryFilterParams());

            formik.handleChange({
                target: {
                    name: 'region',
                    value: '',
                },
            });

            formik.handleChange({
                target: {
                    name: 'branchCode',
                    value: '',
                },
            });

            setSelectedBranchCode('');

            return;
        }

        setSelectedBranchCode(branch.branchCode);

        formik.handleChange({
            target: {
                name: 'branchCode',
                value: branch.branchCode,
            },
        });

        if (branchRegion) {
            formik.handleChange({
                target: {
                    name: 'region',
                    value: branchRegion.regionName,
                },
            });
        }
    };

    const handleOnSelectBranch = (option: TDropdownOption) => {
        const branch = branchesList.find(
            branchOption =>
                branchOption.branchCode ===
                option.value
        );

        const branchRegion = regionList.find(
            regionOption =>
                regionOption.regionId ===
                branch?.regionId
        );

        setSelectedBranchCode(option.value as string);

        formik.handleChange({
            target: {
                name: 'branchCode',
                value: option.value,
            },
        });

        formik.handleChange({
            target: {
                name: 'branchName',
                value: branch?.branchName || '',
            },
        });

        if (branchRegion) {
            formik.handleChange({
                target: {
                    name: 'region',
                    value: branchRegion.regionName,
                },
            });
        }
    };

    const handleOnSearchConvertToProduct = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTargetProductName(
            event.target.value
        );

        const product = inventoryList.find(
            productOption =>
                productOption.productName ===
                event.target.value
        );

        if (!product) {
            formik.setFieldValue(
                'targetProductId',
                ''
            );

            return;
        }

        formik.setFieldValue(
            'targetProductId',
            product.productId
        );

        dispatch(
            combinedActions.goldSetSerialNumberFilterParams(
                {
                    branchCode:
                        formik.values.branchCode,
                    productId:
                        product.productId,
                }
            )
        );

        dispatch(
            combinedActions.goldGetAllSerialNumbersAttempt()
        );
    };

    const handleOnSelectConvertToProduct = (option: TDropdownOption) => {
        setTargetProductName(option.label);

        const product = inventoryList.find(
            productOption =>
                productOption.productId ===
                option.value
        );

        if (!product) {
            formik.setFieldValue(
                'targetProductId',
                ''
            );

            return;
        }

        formik.setFieldValue(
            'targetProductId',
            product.productId
        );

        dispatch(
            combinedActions.goldSetSerialNumberFilterParams(
                {
                    branchCode:
                        formik.values.branchCode,
                    productId:
                        product.productId,
                }
            )
        );

        dispatch(
            combinedActions.goldGetAllSerialNumbersAttempt()
        );
    };

    const computeSerialNo = (
        quantity: number,
        selectedProduct: TInventoryFilter
    ): string => {
        if (
            !selectedProduct ||
            !quantity
        ) {
            return '';
        }

        const availableAmount = selectedProduct.amount;

        if (
            quantity <= 0 ||
            quantity > availableAmount
        ) {
            return '';
        }

        const serials =
            quantity === availableAmount
                ? serialNumberList
                : serialNumberList.slice(
                    0,
                    quantity
                );

        const serialNumbers = serials.map(s => s.serialNo);

        return serialNumbers.length > 1
            ? serialNumbers.join(',')
            : serialNumbers[0] || '';
    };

    const handleOnChangeQuantity = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;

        const selectedProduct =
            inventoryList.find(
                productOption =>
                    productOption.productId ===
                    Number(
                        formik.values.targetProductId
                    )
            );

        formik.setValues({
            ...formik.values,

            quantity: value,

            serialNo: selectedProduct
                ? computeSerialNo(
                    Number(value),
                    selectedProduct
                )
                : formik.values.serialNo,
        });
    };

    const handleQuantityOnBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        formik.handleBlur(event);
    };

    const handleOnSearchRegion = (event: React.ChangeEvent<HTMLInputElement>) => {
        formik.handleChange({ target: { name: 'region', value: event.target.value }});
    };

    const handleOnSelectRegion = (option: TDropdownOption) => {
        formik.handleChange({ target: { name: 'region', value: option.value }});
    };

    const physicalGoldSilverProducts = inventoryList
        .filter(item =>
            (item.goldType === GoldTypeEnum.PhysicalGold ||
            item.silverType === SilverTypeEnum.PhysicalSilver) &&
            Number(item.fundType) === Number(customerDetails?.fundType)
        )
        .filter(item => {
            const isSilver = Number(formik.values.metalType) === Number(MetalTypeEnum.Silver);
            const availableBalance = Number(isSilver ? formik.values.availablePaperSilver : formik.values.availablePaperGold);
            const productWeight = Number(isSilver ? item.physicalSilverWeight : item.physicalGoldWeight);

            return (
                !isNaN(availableBalance) &&
                !isNaN(productWeight) &&
                productWeight <= availableBalance
            );
        })
        .map(inventory => ({ label: inventory.productName, value: inventory.productId }));

    const handleOnSelectMetalType = (option: TDropdownOption) => {
        const selectedMetalType = Number(option.value) as MetalTypeEnum;

        dispatch(combinedActions.goldCreateGoldConversionDraftsAttempt({ metalType: selectedMetalType }));

        isChangingMetalType.current = true;

        formik.setValues({
            ...formik.values,
            metalType: option.value.toString(),
            targetProductId: '',
            quantity: '',
            serialNo: '',
            conversionFee: '',
        });

        setTargetProductName('');

        dispatch(combinedActions.goldClearConversionDraftsFee());

        const paperProduct = customerInventory.find(
            item => selectedMetalType === MetalTypeEnum.Silver ? item.silverType === SilverTypeEnum.PaperSilver : item.goldType === GoldTypeEnum.PaperGold
        );

        formik.setFieldValue('originProductId',paperProduct?.productId?.toString() || '');

        dispatch(
            combinedActions.goldSetInventoryFilterParams({
                ...inventoryFilterParams,
                branchCode: formik.values.branchCode,
                metalType: selectedMetalType,
                goldType: selectedMetalType === MetalTypeEnum.Gold ? GoldTypeEnum.PhysicalGold : undefined,
                silverType: selectedMetalType === MetalTypeEnum.Silver ? SilverTypeEnum.PhysicalSilver : undefined,
            })
        );
        dispatch(combinedActions.goldGetAllInventoriesAttempt());
    };

    return (
        <form
            onSubmit={formik.handleSubmit}
            className={FormStyles.container}
        >
            <div className={FormStyles['form-container']}>
                <LabelledDropdown
                    isFilter
                    label='Search for customer name or ID* : '
                    options={customerOptions}
                    value={name}
                    onChange={handleOnSearchCustomer}
                    onSelectOption={handleOnSelectCustomer}
                    noOptionsMessage={!name ? 'Search for customer name or ID' : 'No customer found'}
                    isLoading={gettingCustomers}
                    autoComplete='off'
                    optionSelected={!!customerOptions.find(option => option.label === name)}
                    inputTestId='conversion-customer-name-input-dropdown'
                    optionTestId='conversion-customer-name-options-list-dropdown'
                />

                <LabelledInput
                    id='name'
                    name='name'
                    label='Customer Name* :'
                    value={formik.values.name}
                    error={
                        formik.touched.name
                            ? formik.errors.name
                            : undefined
                    }
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled
                    className={FormStyles['span-2']}
                />

                <LabelledInput
                    id='idNo'
                    name='idNo'
                    label='ID Number* :'
                    value={formik.values.idNo}
                    error={
                        formik.touched.idNo
                            ? formik.errors.idNo
                            : undefined
                    }
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled
                />

                <LabelledInput
                    id='CIFNo'
                    name='CIFNo'
                    label='CIF No.* :'
                    value={formik.values.CIFNo}
                    error={
                        formik.submitCount > 0 &&
                        formik.touched.CIFNo
                            ? formik.errors.CIFNo
                            : undefined
                    }
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled
                />

                <LabelledDropdown
                    id='branchName'
                    name='branchName'
                    label='Branch*: '
                    options={branchOptions}
                    value={formik.values.branchName}
                    error={
                        formik.touched.branchName
                        ? formik.errors
                                .branchName
                        : undefined
                    }
                    onChange={handleOnSearchBranch}
                    onSelectOption={handleOnSelectBranch}
                    noOptionsMessage='No branches found'
                    autoComplete='off'
                    optionSelected={
                        !!branchOptions.find(
                            branch =>
                                branch.label ===
                                formik.values
                                    .branchName
                        )
                    }
                    disabled={!allBranchAccess}
                />

                <LabelledInput
                    id='branchCode'
                    name='branchCode'
                    label='Branch Code* :'
                    value={formik.values.branchCode}
                    error={formik.touched.branchCode ? formik.errors.branchCode : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled
                />

                <LabelledDropdown
                    id='region'
                    name='region'
                    label='Region* :'
                    options={regionOptions}
                    value={formik.values.region}
                    error={formik.submitCount && formik.touched.region ? formik.errors.region : undefined}
                    onChange={handleOnSearchRegion}
                    onSelectOption={handleOnSelectRegion}
                    onBlur={formik.handleBlur}
                    noOptionsMessage='No Region found'
                    className={FormStyles['span-2']}
                    autoComplete='off'
                    optionSelected={!!regionOptions.find(option => option.label === formik.values.region)}
                    disabled
                />

                <LabelledDropdown
                    id='metalType'
                    name='metalType'
                    label='Metal Type *:'
                    options={metalTypeOptions}
                    value={metalTypeOptions.find(option => option.value === formik.values.metalType)?.label ?? ''}
                    error={formik.touched.metalType && formik.errors.metalType ? formik.errors.metalType : undefined}
                    onSelectOption={handleOnSelectMetalType}
                    optionSelected={!!formik.values.metalType}
                    noOptionsMessage='No metal type found'
                />

                <div className={FormStyles['available-balance']}>
                    <Text label='Available balance:' />
                    <Text
                        label={`${fixedDecimal(
                            Number(formik.values.metalType) ===
                                MetalTypeEnum.Silver
                                ? Number(formik.values.availablePaperSilver)
                                : Number(formik.values.availablePaperGold), 3)} g`}
                        isBold
                    />
                </div>

                <LabelledDropdown
                    id='targetProductId'
                    name='targetProductId'
                    label='Convert To* :'
                    options={physicalGoldSilverProducts.sort((a, b) => a.value - b.value)}
                    value={targetProductName || ''}
                    error={formik.touched.targetProductId? formik.errors.targetProductId : undefined}
                    onChange={handleOnSearchConvertToProduct}
                    onSelectOption={handleOnSelectConvertToProduct}
                    noOptionsMessage='No product found'
                    autoComplete='off'
                    optionSelected={!!physicalGoldSilverProducts.find(product => product.label === targetProductName)}
                    disabled={!formik.values.branchCode}
                />

                <LabelledInput
                    id='conversionFee'
                    name='conversionFee'
                    label='Conversion Fee:'
                    value={goldConversionDraftsFee?.weightedConversionFee !== undefined
                            ? `RM ${fixedDecimal(
                                goldConversionDraftsFee.weightedConversionFee,
                                2
                            )}`
                            : ''
                    }
                    disabled
                />

                <LabelledInput
                    data-testid='create-gold-conversion-quantity-input'
                    id='quantity'
                    name='quantity'
                    label='Quantity*: '
                    value={formik.values.quantity}
                    error={formik.touched.quantity ? formik.errors.quantity : undefined}
                    onChange={handleOnChangeQuantity}
                    onBlur={handleQuantityOnBlur}
                />

                <LabelledInput
                    data-testid='create-gold-conversion-serialNo-input'
                    id='serialNo'
                    name='serialNo'
                    label='Serial Number (comma delimited)* :'
                    value={
                        formik.values.serialNo
                    }
                    error={
                        formik.touched.serialNo
                            ? formik.errors.serialNo
                            : undefined
                    }
                    onChange={
                        formik.handleChange
                    }
                    onBlur={
                        formik.handleBlur
                    }
                />

                <LabelledInput
                    id='salesPersonnelName'
                    name='salesPersonnelName'
                    label='Sales Personnel Name:'
                    value={
                        formik.values
                            .salesPersonnelName
                    }
                    error={
                        formik.touched
                            .salesPersonnelName
                            ? formik.errors
                                .salesPersonnelName
                            : undefined
                    }
                    onChange={
                        formik.handleChange
                    }
                    onBlur={
                        formik.handleBlur
                    }
                />

                <LabelledInput
                    id='referralName'
                    name='referralName'
                    label='Referral Name :'
                    value={
                        formik.values
                            .referralName
                    }
                    error={
                        formik.touched.referralName
                            ? formik.errors
                                .referralName
                            : undefined
                    }
                    onChange={
                        formik.handleChange
                    }
                    onBlur={
                        formik.handleBlur
                    }
                />
            </div>

            <div
                className={`${FormStyles['button-container']} ${FormStyles['span-2']}`}
            >
                <div
                    className={
                        FormStyles[
                            'button-subcontainer'
                        ]
                    }
                >
                    <ActionButton
                        testId='create-conversion-submit-button'
                        label='Continue'
                        type='submit'
                        isLoading={
                            createConversionFinalDraftsIsLoading
                        }
                        disabled={
                            createConversionFinalDraftsIsLoading
                        }
                    />
                </div>
            </div>
        </form>
    );
};

export default CreateGoldConversionForm;
